// aiModelFallback — E-009: picka "nearest" fallback-modell när
// model-gate returnerar 402 (I-004).
//
// När en user har konfigurerat t.ex. `model: 'anthropic/claude-opus-4-7'`
// i localStorage men deras tier bara tillåter free-allowlist returnerar
// edge-fn:en 402 `{ error: 'model-not-allowed', requested, tier, allowed: [...] }`.
// Istället för att bara visa error:et kan vi auto-välja en
// allowed-modell och retryra — med en toast så user:n inte blir
// förvånad av tyst modell-byte.
//
// Fallback-strategin: föredra samma "family" (anthropic/openai/google)
// om möjligt; annars första i allowed-listan. Vi returnerar null om
// ingen fallback-kandidat finns (tom allowed-lista).

export interface ModelFallbackInput {
  requested: string;
  allowed: readonly string[];
}

/** Returns same-family candidate if available, else first allowed, else null. */
export function pickFallbackModel(input: ModelFallbackInput): string | null {
  const { requested, allowed } = input;
  if (!allowed || allowed.length === 0) return null;
  // Extract "family"-prefix: provider-del före "/"
  const familyMatch = requested.match(/^([^/]+)\//);
  const family = familyMatch?.[1]?.toLowerCase();
  if (family) {
    const sameFamilyMatch = allowed.find((m) => m.toLowerCase().startsWith(`${family}/`));
    if (sameFamilyMatch) return sameFamilyMatch;
  }
  return allowed[0];
}

/** Human-readable toast-message for fallback. Caller väljer lang. */
export function fallbackToastMessage(
  requested: string,
  fallback: string,
  lang: 'sv' | 'en' = 'sv',
): string {
  if (lang === 'en') {
    return `Model "${requested}" requires a higher plan — using "${fallback}" instead.`;
  }
  return `Modellen "${requested}" kräver högre plan — använder "${fallback}" istället.`;
}

/** Parse 402-response-body till { requested, allowed }. Tolerant mot
 * okänd shape. */
export function parseModelNotAllowedResponse(
  body: unknown,
): ModelFallbackInput | null {
  if (!body || typeof body !== 'object') return null;
  const obj = body as Record<string, unknown>;
  if (obj.error !== 'model-not-allowed') return null;
  const requested = typeof obj.requested === 'string' ? obj.requested : '';
  const allowed = Array.isArray(obj.allowed)
    ? obj.allowed.filter((x): x is string => typeof x === 'string')
    : [];
  if (!requested || allowed.length === 0) return null;
  return { requested, allowed };
}
